import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import { invalidateBatteryPriceDependents } from "./battery-price-dependents"
import type {
  BatteryProposalDto,
  UpdateBatteryExistingBatteryDto,
} from "../dto/battery-proposal.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

/**
 * Record whether the home already has a battery. LightReach Energy Backup
 * eligibility turns on it, and a change makes a saved LightReach quote stale,
 * so the financing reads refresh with the proposal.
 */
export default function useUpdateBatteryExistingBattery(): UseMutationResult<
  BatteryProposalDto,
  unknown,
  { proposalId: string; body: UpdateBatteryExistingBatteryDto }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }) =>
      batteryProposalService.updateExistingBattery(proposalId, body),
    // A refusal may mean a LightReach application now locks it; re-read the lock.
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.byId(proposalId),
        exact: true,
      })
    },
    onSuccess: (proposal, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.batteryProposal.byId(proposalId),
        proposal,
      )
      invalidateBatteryPriceDependents(queryClient, proposalId)
    },
  })
}
